import type * as Amplitude from "@amplitude/analytics-browser";
import { useEffect, useMemo } from "react";
import { useStytchMember, useStytchOrganization } from "../hooks/stytch-compat";
import { e2ePublish, isE2E } from "../platform/e2e";
import { getPlatform } from "../platform/platformRef";
import { selectedTenantOrgSlug } from "../platform/tenantIdentity";
import { isInIframe } from "../utils/browserUtils";
import { PrimeDebugLog } from "./debugLog";
import type {
    AnalyticsEventParameters,
    ClientAnalyticsEventName,
    EventPropertyDefinition,
} from "./eventRegistry";
import { EVENT_DEFINITIONS, EVENT_VERSION } from "./eventRegistry";
import { productEventQueue } from "./productEventQueue";
import { isTokenPage, routePath } from "./routePath";
import { postProductEvents } from "../api/productEvents";
import { sourceCredentialFetch } from "./authenticatedFetch";
import type { ProductEventEnvelope } from "../shared/interfaces";
import { generateUuid } from "./uuid";

export { AnalyticsEvents } from "./eventRegistry";

/** Never leaves the browser in a first-party event: the server stamps identity from
 *  the session, and the envelope carries the tenant. */
const FIRST_PARTY_OMITTED_KEYS: Record<string, true> = {
    org_slug: true,
    org_name: true,
    user_id: true,
    role: true,
    is_internal: true,
    event_id: true,
    event_version: true,
    occurred_at: true,
};

type AmplitudeModule = typeof Amplitude;
type ProductShell = "web" | "tablet" | "embed" | "native";
type FirstPartyValue = string | number | boolean;

/** The signed-in member: Amplitude's user identity and the identity properties on its
 *  events. The tenant is not part of it. Every host reads the tenant from the platform
 *  seam (`platform.tenant`); the desktop knows it before its first render. */
interface AnalyticsIdentity {
    userId: string;
    orgName: string;
    role: string;
    isInternal: boolean;
}

type AnalyticsTestWindow = typeof window & { __PRIME_ANALYTICS_TEST__?: boolean };

const NO_AMPLITUDE: Promise<AmplitudeModule | null> = Promise.resolve(null);

let setupPromise: Promise<AmplitudeModule | null> | null = null;
let amplitudeWork: Promise<void> = Promise.resolve();
let currentIdentity: AnalyticsIdentity | null = null;
let appliedIdentityKey: string | null = null;
let warnedMissingStamp = false;

/** The shell the page runs in. The page never calls itself "native": the iOS shell's own
 *  Swift events carry that (`NativeAnalytics`), several web events are refused from it, and
 *  reports tell the app's page from a browser by the server's `transport` stamp. */
export function getProductShell(): ProductShell {
    if (typeof window === "undefined") return "web";
    if (isInIframe() || window.location.pathname.startsWith("/embed")) return "embed";
    if (/iPad|Tablet/i.test(window.navigator.userAgent)) return "tablet";
    return "web";
}

function redactedPath(): string {
    if (typeof window === "undefined") return "";
    return routePath(window.location.pathname);
}

/** An absolute URL with its path made the route path; anything else is left as it is. */
function routeUrl(href: string): string {
    const parts = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)([^?#]*)([\s\S]*)$/i.exec(href);
    return parts ? `${parts[1]}${routePath(parts[2] || "/")}${parts[3]}` : href;
}

const AMPLITUDE_PAGE_PATH = "[Amplitude] Page Path";
const AMPLITUDE_PAGE_URLS = [
    "[Amplitude] Page URL",
    "[Amplitude] Page Location",
    "[Amplitude] Previous Page Location",
];

/** Amplitude reads `location` itself for its page properties: its page-view plugin when it
 *  tracks a view, and its page-URL enrichment on every other event, Previous Page Location
 *  included. This gives them the route path the first-party events carry. Enrichments run in
 *  the order they were added, and `init` adds the SDK's own just before it finishes, so this
 *  one is added only after `init` completes (`initializeWithRoutePaths`). */
const routePathEnrichment: Amplitude.Types.EnrichmentPlugin<
    Amplitude.Types.BrowserClient,
    Amplitude.Types.BrowserConfig
> = {
    name: "primepoint-route-path",
    type: "enrichment",
    async execute(event) {
        // A revenue event's typed properties carry no page; every other event's are a record.
        const properties = event.event_properties as Record<string, unknown> | undefined;
        if (!properties) return event;
        const path = properties[AMPLITUDE_PAGE_PATH];
        if (typeof path === "string") properties[AMPLITUDE_PAGE_PATH] = routePath(path);
        for (const name of AMPLITUDE_PAGE_URLS) {
            const url = properties[name];
            if (typeof url === "string") properties[name] = routeUrl(url);
        }
        return event;
    },
};

/**
 * Runs `init`, then adds `routePathEnrichment`, and holds every event until it is added: the
 * hold is a `before` step added ahead of `init`, so it runs first for each event, the first page
 * view included, and that event reaches the enrichments only once the route path is among them.
 * Amplitude handles one event at a time, so the events after it wait behind it.
 */
function initializeWithRoutePaths(amplitude: AmplitudeModule, init: () => Promise<void>): void {
    let installed!: () => void;
    const ready = new Promise<void>((resolve) => (installed = resolve));
    amplitude.add({
        name: "primepoint-route-path-hold",
        type: "before",
        async execute(event) {
            await ready;
            return event;
        },
    });
    void init()
        // A failed init still releases the hold.
        .catch(() => undefined)
        .then(() => amplitude.add(routePathEnrichment).promise)
        .catch(() => undefined)
        .finally(installed);
}

/**
 * The organization stamp: the tenant the host already knows (the Stytch organization
 * or the embed cache on the web, the native auth slug on the desktop). Read at event
 * time, so an event carries the tenant of the moment it was emitted, and kept in step
 * with the first-party queue, which drains only the active organization.
 */
function tenantStamp(): string | null {
    // Not `tenant.orgSlug()`: under a canonical URL scope that is a `scope:` storage key,
    // which the events API rejects and Amplitude must never see.
    const orgSlug = selectedTenantOrgSlug(getPlatform().tenant.orgSlug());
    productEventQueue.setActiveOrganization(orgSlug);
    return orgSlug;
}

function commonProperties(
    orgSlug: string | null,
    identity: AnalyticsIdentity | null,
): Record<string, unknown> {
    const properties: Record<string, unknown> = {
        shell: getProductShell(),
        path: redactedPath(),
    };
    if (orgSlug) properties.org_slug = orgSlug;
    if (identity) {
        properties.org_name = identity.orgName;
        properties.role = identity.role;
        properties.is_internal = identity.isInternal;
    }
    return properties;
}

async function initializeAmplitude(apiKey: string): Promise<AmplitudeModule | null> {
    try {
        // Loaded on demand: the SDK is its own chunk, fetched only on a host that
        // reports to an Amplitude project, and never on the desktop.
        const amplitude = await import("@amplitude/analytics-browser");
        initializeWithRoutePaths(
            amplitude,
            () =>
                amplitude.init(apiKey, undefined, {
                    autocapture: {
                        elementInteractions: false,
                        // Keep campaign attribution on the user profile.
                        attribution: { trackingMethod: "userProperty" },
                    },
                }).promise,
        );
        return amplitude;
    } catch (error) {
        console.error("[Analytics] Failed to load Amplitude:", error);
        return null;
    }
}

/** A signed-out page whose URL is a bearer token: the outside-party guest page and a
 *  product update email's stop page. */
function isGuestPage(): boolean {
    if (typeof window === "undefined") return false;
    return isTokenPage(window.location.pathname);
}

/** The Amplitude SDK, loaded once, or `null` on a host whose page reports to no
 *  Amplitude project (`Platform.amplitudeApiKey`): the SDK is then never loaded. */
function ensureAmplitude(): Promise<AmplitudeModule | null> {
    // Never initialize on an outside-party guest page. Autocapture's automatic
    // page-view events carry the full URL, and on /guest/<token> that URL is a
    // bearer credential for reading the record and its files and submitting the
    // response. The page also mounts above AppContent, so useSetupAnalytics does
    // not run there; this is the second lock on the same door.
    if (isGuestPage()) return NO_AMPLITUDE;
    const apiKey = getPlatform().amplitudeApiKey;
    if (!apiKey) return NO_AMPLITUDE;
    if (!setupPromise) setupPromise = initializeAmplitude(apiKey);
    return setupPromise;
}

function identityKey(identity: AnalyticsIdentity, orgSlug: string | null): string {
    return [
        identity.userId,
        orgSlug ?? "",
        identity.orgName,
        identity.role,
        String(identity.isInternal),
    ].join("|");
}

function applyAmplitudeIdentity(
    amplitude: AmplitudeModule,
    identity: AnalyticsIdentity | null,
    orgSlug: string | null,
): void {
    if (!identity) {
        if (appliedIdentityKey) amplitude.reset();
        appliedIdentityKey = null;
        return;
    }

    const nextKey = identityKey(identity, orgSlug);
    if (nextKey === appliedIdentityKey) return;
    amplitude.setUserId(identity.userId);
    const identify = new amplitude.Identify()
        .set("org_name", identity.orgName)
        .set("role", identity.role)
        .set("is_internal", identity.isInternal);
    if (orgSlug) {
        amplitude.setGroup("org", orgSlug);
        identify.set("org_slug", orgSlug);
    }
    amplitude.identify(identify);
    appliedIdentityKey = nextKey;
}

function enqueueAmplitudeWork(work: (amplitude: AmplitudeModule) => void): Promise<void> {
    const operation = amplitudeWork
        .catch(() => undefined)
        .then(async () => {
            const amplitude = await ensureAmplitude();
            if (amplitude) work(amplitude);
        });
    amplitudeWork = operation;
    return operation;
}

export function configureAnalyticsIdentity(identity: AnalyticsIdentity | null): void {
    currentIdentity = identity;
    const orgSlug = tenantStamp();
    if (identity && identityKey(identity, orgSlug) === appliedIdentityKey) return;
    void enqueueAmplitudeWork((amplitude) => applyAmplitudeIdentity(amplitude, identity, orgSlug));
}

export function useSetupAnalytics(): boolean {
    const { member } = useStytchMember();
    const { organization } = useStytchOrganization();
    const role = useMemo(
        () =>
            member?.roles
                .map(({ role_id }) => role_id)
                .sort()
                .join(",") || "member",
        [member?.roles],
    );

    useEffect(() => {
        void ensureAmplitude();
    }, []);

    useEffect(() => {
        if (!member || !organization) {
            configureAnalyticsIdentity(null);
            return;
        }
        configureAnalyticsIdentity({
            userId: member.email_address || member.member_id,
            orgName: organization.organization_name || organization.organization_slug,
            role,
            isInternal: member.email_address.toLowerCase().endsWith("@primepoint.ai"),
        });
    }, [member, organization, role]);

    return Boolean(member && organization);
}

function isFirstPartyValue(value: unknown): value is FirstPartyValue {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function firstPartyProperties(
    properties: Record<string, unknown>,
    contract: Readonly<Record<string, EventPropertyDefinition>>,
): Record<string, FirstPartyValue> {
    const result: Record<string, FirstPartyValue> = {};
    for (const [key, value] of Object.entries(properties)) {
        const allowed = key === "shell" || key === "path" || Object.hasOwn(contract, key);
        if (allowed && !FIRST_PARTY_OMITTED_KEYS[key] && isFirstPartyValue(value)) {
            result[key] = value;
        }
    }
    return result;
}

/** A first-party event with no tenant to stamp it is dropped. The first drop of the
 *  session says so once; a signed-out host emits nothing worth a line per event. */
function dropUnstamped(eventName: string): void {
    if (warnedMissingStamp) return;
    warnedMissingStamp = true;
    console.warn(
        `[Analytics] No organization stamp; dropping first-party events until one exists (first: ${eventName})`,
    );
}

export async function analyticsEvent<Name extends ClientAnalyticsEventName>(
    eventName: Name,
    ...[params]: AnalyticsEventParameters<Name>
): Promise<void> {
    await emitEvent(eventName, params, tenantStamp, (envelope) =>
        productEventQueue.enqueue(envelope),
    );
}

/**
 * An event from a project URL tab that holds no admitted project credential, such as one
 * showing an admission notice. Until admission the tenant stamp is empty and the Document's
 * request fence lets nothing but bootstrap out, so this event is stamped with the organization
 * of the browser's own sign-in and posted at once on that sign-in, outside the tenant queue.
 */
export async function sourceAnalyticsEvent<Name extends ClientAnalyticsEventName>(
    eventName: Name,
    sourceOrgSlug: string | null,
    ...[params]: AnalyticsEventParameters<Name>
): Promise<void> {
    await emitEvent(
        eventName,
        params,
        () => sourceOrgSlug,
        (envelope) =>
            postProductEvents(
                { org_slug: envelope.org_slug, events: [envelope] },
                true,
                sourceCredentialFetch,
            ),
    );
}

async function emitEvent(
    eventName: ClientAnalyticsEventName,
    params: unknown,
    stamp: () => string | null,
    deliver: (envelope: ProductEventEnvelope) => Promise<unknown>,
): Promise<void> {
    if (
        process.env.NODE_ENV === "test" &&
        !(window as AnalyticsTestWindow).__PRIME_ANALYTICS_TEST__
    ) {
        return;
    }
    const definition = EVENT_DEFINITIONS.get(eventName);
    if (!definition || definition.owner !== "client") {
        console.error(
            `[Analytics] Refusing unregistered or server-owned client event: ${eventName}`,
        );
        return;
    }

    const productShell = getProductShell();
    if (definition.platforms && !definition.platforms.includes(productShell)) {
        console.error(`[Analytics] Refusing ${eventName} from unsupported shell: ${productShell}`);
        return;
    }

    // The stamp is resolved before anything is built: an event with no tenant costs
    // no id, no timestamp and no properties.
    const orgSlug = stamp();
    if (definition.sinks.includes("product_event") && orgSlug === null) {
        dropUnstamped(eventName);
        return;
    }
    const identity = currentIdentity;
    const id = generateUuid();
    const occurredAt = new Date().toISOString();
    const callerProperties = (params ?? {}) as Record<string, unknown>;
    const { path, shell } = callerProperties;
    const properties = {
        ...callerProperties,
        // Identity fields are authoritative and applied after caller properties so
        // no call site can accidentally or deliberately override the tenant stamp.
        ...commonProperties(orgSlug, identity),
        ...(typeof path === "string" ? { path } : {}),
        ...(typeof shell === "string" ? { shell } : {}),
        event_id: id,
        event_version: EVENT_VERSION,
        occurred_at: occurredAt,
    };
    PrimeDebugLog("Analytics", `Event: ${eventName}`, properties);

    const deliveries: Promise<unknown>[] = [];
    if (definition.sinks.includes("amplitude")) {
        deliveries.push(
            enqueueAmplitudeWork((amplitude) => {
                applyAmplitudeIdentity(amplitude, identity, orgSlug);
                amplitude.track(eventName, properties);
            }),
        );
    }

    if (definition.sinks.includes("product_event") && orgSlug !== null) {
        const contractProperties = firstPartyProperties(properties, definition.properties);
        if (isE2E()) {
            // A test run never posts usage events under the owner's identity; the
            // hook reads them from the seam instead.
            e2ePublish.analytics({
                name: eventName,
                orgSlug,
                eventId: id,
                occurredAt,
                properties: contractProperties,
            });
        } else {
            deliveries.push(
                deliver({
                    event_id: id,
                    org_slug: orgSlug,
                    event_name: eventName,
                    event_version: EVENT_VERSION,
                    source: "client",
                    occurred_at: occurredAt,
                    properties: contractProperties,
                }),
            );
        }
    }

    const results = await Promise.allSettled(deliveries);
    for (const result of results) {
        if (result.status === "rejected") {
            console.error("[Analytics] Failed to emit event:", eventName, result.reason);
        }
    }
}
